/**
 * Add Lead Modal — Creates provider-owned leads via backend API.
 * All lead persistence is backend-driven. No local state creation.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, UserPlus, Phone, Mail, Globe, Building2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import api from '../../../utils/api';
// TREATX-UAT-PHONE-BULK-MIGRATION-02 — canonical E.164 phone input.
import { PhoneInput } from '../../../components/ui/phone-input';

// Source channels (display-only constants)
// SOURCE_CHANNELS are backend enum values; display labels resolved via i18n at render.
const SOURCE_CHANNEL_VALUES = ['SELF_SOURCED','SOCIAL_MEDIA','REFERRAL','WALK_IN','PHONE_INQUIRY','WEBSITE','EVENT','PARTNER','OTHER'];
const SOURCE_CHANNEL_I18N_KEY = {
  SELF_SOURCED: 'SELF_SOURCED', SOCIAL_MEDIA: 'SOCIAL_MEDIA',
  REFERRAL: 'REFERRAL_LONG', WALK_IN: 'WALK_IN',
  PHONE_INQUIRY: 'PHONE_INQUIRY_LONG', WEBSITE: 'WEBSITE_LONG',
  EVENT: 'EVENT_LONG', PARTNER: 'PARTNER_LONG', OTHER: 'OTHER',
};

// TREATX-SSOT-V-5 — treatment list loaded dynamically from the
// canonical Master Medical Catalog via `/api/public/catalog/treatments`
// (filtered by `status=published AND isActive=true` server-side). No
// hardcoded catalog values.

const initialFormState = {
  patient_name: '',
  patient_email: '',
  patient_phone: '',
  patient_country: '',
  treatment_interest: '',
  source_channel: 'SELF_SOURCED',
  notes: '',
  budget_range: '',
  assigned_agent_id: '',
};

const AddLeadModal = ({ isOpen, onClose, onCreated, clinicId, salesReps = [], allowInvite = false }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [COUNTRIES, setCOUNTRIES] = useState([]);
  // TREATX-SSOT-V-5 — dynamically loaded canonical treatment catalog.
  const [treatments, setTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(true);
  const [treatmentsError, setTreatmentsError] = useState(null);

  useEffect(() => {
    api.get(`/public/catalog/countries`)
      .then(({ data: d }) => { if (d.success) setCOUNTRIES(d.countries); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let alive = true;
    setTreatmentsLoading(true);
    setTreatmentsError(null);
    api.get('/public/catalog/treatments')
      .then(({ data: d }) => {
        if (!alive) return;
        const list = Array.isArray(d?.treatments) ? d.treatments : [];
        // Canonical name resolution (name may be a localized object).
        const names = list
          .map(t => (typeof t.name === 'string' ? t.name : (t.name?.en || t.slug)))
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setTreatments(names);
      })
      .catch((err) => {
        if (!alive) return;
        setTreatmentsError(err?.message || 'Failed to load treatments');
      })
      .finally(() => { if (alive) setTreatmentsLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormState);
      setErrors({});
      setIsSubmitting(false);
      setApiError(null);
    }
  }, [isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    if (apiError) setApiError(null);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.patient_name.trim()) newErrors.patient_name = t('provider.leads.addLead.validationNameReq');
    if (!formData.treatment_interest) newErrors.treatment_interest = t('provider.leads.addLead.validationTreatmentReq');
    if (!formData.patient_email && !formData.patient_phone) {
      newErrors.contact = t('provider.leads.addLead.validationContactReq');
    }
    if (formData.patient_email && !/\S+@\S+\.\S+/.test(formData.patient_email)) {
      newErrors.patient_email = t('provider.leads.addLead.validationEmailFmt');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e, { invite = false } = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validate()) return;
    // OWNER-CRM-INVITE-01 — inviting is OPTIONAL and needs a usable e-mail.
    if (invite && !formData.patient_email) {
      setErrors((prev) => ({ ...prev, patient_email: t('addPatient.inviteNeedsEmail', 'An email address is required to invite this patient') }));
      return;
    }

    setIsSubmitting(true);
    setApiError(null);
    try {
      // The lead is persisted FIRST; the optional invitation is attempted
      // afterwards by the parent, so a failed invitation never loses the lead
      // and a retry never creates a second one.
      await onCreated({ ...formData, invite_after_create: invite });
      setFormData(initialFormState);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || t('provider.leads.addLead.validationApiDefault');
      setApiError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="add-lead-modal">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <CardHeader className="sticky top-0 bg-white z-10 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <UserPlus className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <CardTitle className="text-lg">{t('addPatient.title', 'Add New Patient Lead')}</CardTitle>
                <p className="text-sm text-gray-500">{t('addPatient.subtitle', 'Create a clinic-sourced lead')}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="close-add-lead-modal">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {apiError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="add-lead-error">
                {apiError}
              </div>
            )}

            {/* Provider-owned badge */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Building2 className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800" data-testid="add-lead-clinic-owned-note">{t('addPatient.clinicOwnedNote')}</span>
            </div>

            {/* Section 1: Patient Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm">1</span>
                {t('addPatient.patientInfo.title', 'Patient Information')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="patient_name">{t('addPatient.patientInfo.fullName', 'Full Name')} *</Label>
                  <Input
                    id="patient_name"
                    data-testid="lead-patient-name"
                    value={formData.patient_name}
                    onChange={(e) => handleChange('patient_name', e.target.value)}
                    placeholder={t('provider.leads.addLead.placeholderName')}
                    className={errors.patient_name ? 'border-red-500' : ''}
                  />
                  {errors.patient_name && <p className="text-red-500 text-xs mt-1">{errors.patient_name}</p>}
                </div>
                <div>
                  <Label htmlFor="patient_email">{t('addPatient.patientInfo.email', 'Email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="patient_email"
                      data-testid="lead-patient-email"
                      type="email"
                      value={formData.patient_email}
                      onChange={(e) => handleChange('patient_email', e.target.value)}
                      placeholder={t('provider.leads.addLead.placeholderEmail')}
                      className={`pl-10 ${errors.patient_email ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {errors.patient_email && <p className="text-red-500 text-xs mt-1">{errors.patient_email}</p>}
                </div>
                <div>
                  <Label htmlFor="patient_phone">{t('addPatient.patientInfo.phone', 'Phone')}</Label>
                  <PhoneInput
                    value={formData.patient_phone}
                    onChange={(e164) => handleChange('patient_phone', e164)}
                    testId="lead-patient-phone"
                    placeholder={t('provider.leads.addLead.placeholderPhone')}
                  />
                </div>
                {errors.contact && <p className="text-red-500 text-xs sm:col-span-2">{errors.contact}</p>}
                <div>
                  <Label htmlFor="patient_country">{t('addPatient.patientInfo.country', 'Country')}</Label>
                  <select
                    id="patient_country"
                    data-testid="lead-patient-country"
                    value={formData.patient_country}
                    onChange={(e) => handleChange('patient_country', e.target.value)}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="">{t('provider.leads.addLead.selectCountry')}</option>
                    {COUNTRIES.map(c => <option key={c.code || c} value={c.name || c}>{c.name || c}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Section 2: Treatment */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm">2</span>
                {t('addPatient.treatmentDetails.title', 'Treatment Details')}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="treatment_interest">{t('provider.leads.addLead.treatmentType')} *</Label>
                  <select
                    id="treatment_interest"
                    data-testid="lead-treatment"
                    value={formData.treatment_interest}
                    onChange={(e) => handleChange('treatment_interest', e.target.value)}
                    disabled={treatmentsLoading || !!treatmentsError}
                    className={`w-full p-2 border rounded-md text-sm ${errors.treatment_interest ? 'border-red-500' : ''}`}
                  >
                    <option value="">
                      {treatmentsLoading
                        ? t('provider.leads.addLead.loadingTreatments')
                        : treatmentsError
                          ? t('provider.leads.addLead.treatmentsLoadError')
                          : treatments.length === 0
                            ? t('provider.leads.addLead.noTreatmentsAvail')
                            : t('provider.leads.addLead.selectTreatment')}
                    </option>
                    {treatments.map(t => <option key={t} value={t}>{t}</option>)}
                    <option value="Other">{t('provider.leads.addLead.other')}</option>
                  </select>
                  {treatmentsError && (
                    <p className="text-red-500 text-xs mt-1" data-testid="lead-treatment-error">
                      {t('provider.leads.addLead.treatmentsErrorInline', { err: treatmentsError })}
                    </p>
                  )}
                  {errors.treatment_interest && <p className="text-red-500 text-xs mt-1">{errors.treatment_interest}</p>}
                </div>
                <div>
                  <Label htmlFor="budget_range">{t('provider.leads.addLead.budgetRange')}</Label>
                  <select
                    id="budget_range"
                    data-testid="lead-budget"
                    value={formData.budget_range}
                    onChange={(e) => handleChange('budget_range', e.target.value)}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="">{t('provider.leads.addLead.selectBudget')}</option>
                    <option value="1000-2000 EUR">1,000 - 2,000 EUR</option>
                    <option value="2000-3000 EUR">2,000 - 3,000 EUR</option>
                    <option value="3000-5000 EUR">3,000 - 5,000 EUR</option>
                    <option value="5000-8000 EUR">5,000 - 8,000 EUR</option>
                    <option value="8000-15000 EUR">8,000 - 15,000 EUR</option>
                    <option value="15000+ EUR">15,000+ EUR</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 3: Lead Source */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm">3</span>
                {t('addPatient.leadSource.title', 'Lead Source')}
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {SOURCE_CHANNEL_VALUES.map(val => (
                  <button
                    key={val}
                    type="button"
                    data-testid={`source-channel-${val}`}
                    onClick={() => handleChange('source_channel', val)}
                    className={`p-2 text-sm border rounded-lg transition-colors ${
                      formData.source_channel === val
                        ? 'bg-amber-100 border-amber-400 text-amber-800'
                        : 'hover:bg-gray-50 border-gray-200'
                    }`}
                  >
                    {t(`provider.leads.sourceChannels.${SOURCE_CHANNEL_I18N_KEY[val]}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Section 4: Assignment */}
            {salesReps.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm">4</span>
                  {t('provider.leads.addLead.assignmentTitle')}
                </h3>
                <div>
                  <Label htmlFor="assigned_agent_id">{t('provider.leads.addLead.assignToRep')}</Label>
                  <select
                    id="assigned_agent_id"
                    data-testid="lead-assigned-rep"
                    value={formData.assigned_agent_id}
                    onChange={(e) => handleChange('assigned_agent_id', e.target.value)}
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="">{t('provider.leads.addLead.unassigned')}</option>
                    {salesReps.map(r => (
                      <option key={r.user_id || r.rep_id} value={r.user_id || r.rep_id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Section 5: Notes */}
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <span className="w-6 h-6 bg-gray-100 text-gray-600 rounded-full flex items-center justify-center text-sm">{salesReps.length > 0 ? '5' : '4'}</span>
                {t('addPatient.notes.title', 'Notes')}
              </h3>
              <textarea
                data-testid="lead-notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder={t('provider.leads.addLead.notesPh')}
                className="w-full p-3 border rounded-lg text-sm min-h-[80px]"
              />
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                data-testid="submit-add-lead"
                disabled={isSubmitting}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {isSubmitting ? t('provider.leads.addLead.creating') : t('addPatient.createLead', 'Create Lead')}
              </Button>
              <Button
                type="button"
                data-testid="submit-add-lead-and-invite"
                disabled={isSubmitting || !allowInvite}
                onClick={(e) => handleSubmit(e, { invite: true })}
                variant="outline"
                className={`flex-1 border-amber-600 text-amber-700 hover:bg-amber-50${allowInvite ? '' : ' hidden'}`}
                title={t('addPatient.createAndInviteHint', 'TreatX membership is optional — you can invite later too')}
              >
                {t('addPatient.createAndInvite', 'Create & invite to TreatX')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddLeadModal;
